/**
 * Funnel Resource - API client for funnel navigation and session management
 */

import { ApiClient } from './apiClient';

/**
 * Funnel action types enum
 * 
 * Special types:
 * - DIRECT_NAVIGATION: Bypasses step conditions, navigates directly to targetStepId
 * - BACK_NAVIGATION: Similar to direct_navigation, used for backward navigation
 * 
 * Common types (for logging/tracking):
 * - These are commonly used action types that align with funnel orchestrator conditions
 * - Navigation logic is based on step conditions.from, action types are primarily for analytics
 */
export enum FunnelActionType {
    // Special navigation types (bypass step conditions)
    DIRECT_NAVIGATION = 'direct_navigation',
    BACK_NAVIGATION = 'back_navigation',

    // User interaction types
    CONTINUE_CLICKED = 'continue_clicked',
    BUTTON_CLICK = 'button_click',
    FORM_SUBMIT = 'form_submit',

    // Payment actions
    PAYMENT_SUCCESS = 'payment_success',
    PAYMENT_FAILED = 'payment_failed',

    // Offer actions
    OFFER_ACCEPTED = 'offer_accepted',
    OFFER_DECLINED = 'offer_declined',

    // Cart actions
    CART_UPDATED = 'cart_updated',
    CART_ITEM_ADDED = 'cart_item_added',

    // Generic/custom actions
    CUSTOM = 'custom'
}

/**
 * Data structures for specific action types
 */

export type NextAction<T> = T & { [key: string]: unknown };

// ============================================================================
// SCALABLE RESOURCE SYSTEM
// ============================================================================

/**
 * Base resource data type - all resources must have an id and optional metadata
 */
export interface FunnelResourceData {
    id: string;
    [key: string]: unknown;
}

/**
 * Resource metadata for tracking, versioning, and relationships
 */
export interface ResourceMetadata {
    /** Plugin that created this resource */
    source?: string;
    /** Schema version for this resource */
    version?: string;
    /** Timestamp when resource was created */
    timestamp?: number;
    /** Scope: how long should this resource persist */
    scope?: 'global' | 'session' | 'step' | 'ephemeral';
    /** Tags for categorization and filtering */
    tags?: string[];
    /** Related resource IDs (for relationships) */
    relatedTo?: string[];
    /** Custom metadata */
    [key: string]: unknown;
}

/**
 * Wrapped resource with metadata
 */
export interface ResourceWithMetadata<T extends FunnelResourceData = FunnelResourceData> {
    data: T;
    meta: ResourceMetadata;
}

/**
 * Standard resource types for e-commerce (provides IntelliSense)
 * These are SUGGESTIONS, not limitations - any key is allowed via index signature
 */
export interface StandardResourceKeys {
    // Core commerce entities (hot context)
    order?: FunnelResourceData;
    customer?: FunnelResourceData;
    payment?: FunnelResourceData;
    checkout?: FunnelResourceData;
    cart?: FunnelResourceData;
    subscription?: FunnelResourceData;
    product?: FunnelResourceData;
    variant?: FunnelResourceData;

    // Named resources (persistent reference)
    mainOrder?: FunnelResourceData;
    upsellOrder?: FunnelResourceData;
    downsellOrder?: FunnelResourceData;

    // Collections (array support for multiple instances)
    orders?: FunnelResourceData[];
    customers?: FunnelResourceData[];
    payments?: FunnelResourceData[];
}

/**
 * Flexible resource map supporting:
 * 1. Single resources (order)
 * 2. Collections (orders[])
 * 3. Wrapped resources with metadata
 * 4. Completely custom keys
 * 
 * @example Simple usage (backward compatible)
 * ```typescript
 * resources: {
 *   order: { id: '123', amount: 100 }
 * }
 * ```
 * 
 * @example Multiple instances via collections
 * ```typescript
 * resources: {
 *   order: { id: 'main' },  // Hot context
 *   orders: [               // Full collection
 *     { id: 'main', type: 'initial' },
 *     { id: 'upsell1', type: 'addon' }
 *   ]
 * }
 * ```
 * 
 * @example With metadata (opt-in)
 * ```typescript
 * resources: {
 *   order: {
 *     data: { id: '123', amount: 100 },
 *     meta: { source: 'checkout-plugin', version: '2.0' }
 *   }
 * }
 * ```
 * 
 * @example Typed resources (opt-in strict typing)
 * ```typescript
 * interface MyResources {
 *   order: Order;  // Your typed Order interface
 *   customer: Customer;
 * }
 * 
 * const result = await funnel.next<MyResources>({
 *   type: 'payment_success',
 *   data: {
 *     resources: {
 *       order: { id: '123', amount: 100 } // ✅ Type-checked!
 *     }
 *   }
 * });
 * ```
 */
export type FunnelResourceMap<TCustom = {}> = StandardResourceKeys & {
    /**
     * Fully extensible - any key allowed
     * Value can be:
     * - Single resource: { id: '...' }
     * - Collection: [{ id: '...' }, ...]
     * - Wrapped: { data: {...}, meta: {...} }
     */
    [key: string]:
    | FunnelResourceData
    | FunnelResourceData[]
    | ResourceWithMetadata
    | ResourceWithMetadata[]
    | undefined;
} & TCustom;

/**
 * Typed resources structure for funnel actions
 * 
 * 💡 RESOURCE PROTOCOL: Flexible & Scalable
 * 
 * The system supports multiple patterns for maximum flexibility:
 * 
 * 1. **Simple Pattern** (Default - Backward Compatible):
 *    ```typescript
 *    resources: { order: { id: '123' } }
 *    ```
 * 
 * 2. **Aliasing Pattern** (For Hot + Named Context):
 *    ```typescript
 *    resources: {
 *      order: { id: '123' },      // Hot context (latest)
 *      mainOrder: { id: '123' }   // Named (persistent)
 *    }
 *    ```
 * 
 * 3. **Collection Pattern** (For Multiple Instances):
 *    ```typescript
 *    resources: {
 *      orders: [
 *        { id: 'main', label: 'Initial Order' },
 *        { id: 'upsell1', label: 'Upsell #1' }
 *      ]
 *    }
 *    ```
 * 
 * 4. **Metadata Pattern** (For Versioning/Tracking):
 *    ```typescript
 *    resources: {
 *      order: {
 *        data: { id: '123', amount: 100 },
 *        meta: { version: '2.0', source: 'checkout' }
 *      }
 *    }
 *    ```
 * 
 * 5. **Namespaced Pattern** (For Collision Prevention):
 *    ```typescript
 *    resources: {
 *      'checkout:order': { id: '123' },
 *      'subscription:order': { id: '456' }
 *    }
 *    ```
 * 
 * 6. **Typed Pattern** (For Strict Type Safety):
 *    ```typescript
 *    interface StrictResources {
 *      order: Order;  // Your interface
 *      customer: Customer;
 *    }
 *    funnel.next<StrictResources>({ ... })
 *    ```
 * 
 * All patterns work together - choose what fits your use case!
 */
export interface FunnelActionResources<TCustom = {}> {
    /**
     * Resource map - infinitely extensible
     * - Standard keys provide IntelliSense
     * - Custom keys allowed via index signature
     * - Supports single resources, collections, and metadata
     * - Optional: pass generic for strict typing
     */
    resources?: FunnelResourceMap<TCustom>;
    /**
     * Legacy top-level data (backward compatible)
     * Will be automatically migrated to resources by orchestrator
     */
    [key: string]: unknown;
}

export interface DirectNavigationActionData extends FunnelActionResources {
    targetStepId: string;
}

export interface BackNavigationActionData extends FunnelActionResources {
    targetStepId: string;
}

export interface PaymentSuccessActionData extends FunnelActionResources {
    payment: {
        id: string;
        status: string;
        transactionId?: string;
        timestamp?: string;
        [key: string]: unknown;
    };
    order: {
        id: string;
        amount: number;
        currency: string;
        [key: string]: unknown;
    };
}

export interface PaymentFailedActionData extends FunnelActionResources {
    payment: {
        id: string;
        status: string;
        error?: string;
        [key: string]: unknown;
    };
}

export interface OfferAcceptedActionData extends FunnelActionResources {
    offer: {
        accepted: boolean;
        offerId?: string;
        [key: string]: unknown;
    };
}

export interface OfferDeclinedActionData extends FunnelActionResources {
    offer: {
        declined: boolean;
        offerId?: string;
        [key: string]: unknown;
    };
}

export interface CartUpdatedActionData extends FunnelActionResources {
    cart: {
        hasSpecificItem?: boolean;
        itemIds?: string[];
        variantIds?: string[];
        total?: number;
        [key: string]: unknown;
    };
}

export interface FormSubmitActionData extends FunnelActionResources {
    form?: {
        [key: string]: unknown;
    };
}

/**
 * Base properties shared by all FunnelAction types
 */
interface BaseFunnelAction {
    /**
     * Action timestamp (ISO string format)
     */
    timestamp?: string;
    /**
     * Current URL for session synchronization (browser back/forward handling)
     * If not provided, SDK will automatically use window.location.href
     * @example '/checkout', 'https://store.com/payment'
     */
    currentUrl?: string;
}

/**
 * Discriminated union for FunnelAction based on action type
 */
export type FunnelAction =
    BaseFunnelAction & (({
        type: FunnelActionType.DIRECT_NAVIGATION;
        data: NextAction<DirectNavigationActionData>;
    })
        | ({
            type: FunnelActionType.BACK_NAVIGATION;
            data: NextAction<BackNavigationActionData>;
        })
        | ({
            type: FunnelActionType.CONTINUE_CLICKED;
            data: NextAction<FunnelActionResources>;
        })
        | ({
            type: FunnelActionType.BUTTON_CLICK;
            data: NextAction<FunnelActionResources>;
        })
        | ({
            type: FunnelActionType.FORM_SUBMIT;
            data: NextAction<FormSubmitActionData>;
        })
        | ({
            type: FunnelActionType.PAYMENT_SUCCESS;
            data: NextAction<PaymentSuccessActionData>;
        })
        | ({
            type: FunnelActionType.PAYMENT_FAILED;
            data: NextAction<PaymentFailedActionData>;
        })
        | ({
            type: FunnelActionType.OFFER_ACCEPTED;
            data: NextAction<OfferAcceptedActionData>;
        })
        | ({
            type: FunnelActionType.OFFER_DECLINED;
            data: NextAction<OfferDeclinedActionData>;
        })
        | ({
            type: FunnelActionType.CART_UPDATED;
            data: NextAction<CartUpdatedActionData>;
        })
        | ({
            type: FunnelActionType.CART_ITEM_ADDED;
            data: NextAction<CartUpdatedActionData>;
        })
        | ({
            type: FunnelActionType.CUSTOM;
            data: NextAction<FunnelActionResources>;
        })
        | ({
            type: string;
            data?: NextAction<FunnelActionResources>;
        }));

export interface FunnelNavigationAction {
    type: 'redirect' | 'replace' | 'push' | 'external' | 'none';
    url?: string;
    data?: Record<string, unknown>;
}

export interface FunnelNavigationResult {
    stepId?: string; // Optional for fire-and-forget mode
    url?: string; // URL to redirect to after navigation (if step has a URL configured)
    action?: FunnelNavigationAction; // Optional for fire-and-forget mode
    context?: SimpleFunnelContext; // Optional for fire-and-forget mode
    tracking?: {
        from: string;
        to: string;
        event: string;
        timestamp: string;
    };
    /**
     * Fire-and-forget response: indicates navigation was queued
     * When true, stepId, url, action, and context will be undefined
     */
    queued?: boolean;
    /**
     * Session ID (may be different if session was recovered)
     */
    sessionId?: string;
}

/**
 * Funnel context available to plugins
 * Contains current state, resources, and metadata about the funnel session
 * 
 * @example Basic usage
 * ```typescript
 * const context = await funnel.getContext();
 * const orderId = context.resources?.order?.id;
 * ```
 * 
 * @example Typed usage (opt-in strict typing)
 * ```typescript
 * interface MyResources {
 *   order: Order;
 *   customer: Customer;
 * }
 * const context = await funnel.getContext<MyResources>();
 * const total = context.resources?.order.amount; // ✅ Type-safe!
 * ```
 * 
 * @example Collection access
 * ```typescript
 * const allOrders = context.resources?.orders; // FunnelResourceData[]
 * const mainOrder = allOrders?.find(o => o.id === 'main');
 * ```
 */
export interface SimpleFunnelContext<TCustom = {}> {
    customerId: string;
    storeId: string;
    sessionId: string;
    funnelId: string;
    currentStepId: string;
    previousStepId?: string;
    /**
     * Furthest step reached in the funnel (for progress tracking in cyclic funnels)
     * Only moves forward, never backward - used for analytics
     */
    furthestStepId?: string;
    /**
     * ✅ Environment context (staging or production)
     * - Determined at session initialization based on entry URL
     * - Ensures all navigation stays in the same environment
     * - 'staging': Uses funnel.config (alias domains like funnel--store.cdn.tagada.io)
     * - 'production': Uses funnel.productionConfig (custom domains)
     */
    environment?: 'staging' | 'production';
    startedAt: number;
    lastActivityAt: number;

    /**
     * Typed resources map - infinitely extensible
     * - Single resources, collections, or wrapped with metadata
     * - Pass generic for strict typing: SimpleFunnelContext<MyResources>
     * - Standard keys provide IntelliSense, custom keys always allowed
     */
    resources?: FunnelResourceMap<TCustom>;

    /**
     * Static resources from plugin manifest (type: "static")
     * - Configured in funnel editor's Static Resources tab
     * - Available at runtime as context.static
     * - Example: context.static.offer.id for statically configured offers
     */
    static?: Record<string, unknown>;

    /**
     * Legacy/Custom metadata
     * For backward compatibility and flexible unstructured data
     */
    metadata?: Record<string, unknown>;

    script?: string;
}

export interface FunnelInitializeRequest {
    cmsSession: {
        customerId: string;
        storeId: string;
        sessionId: string;
        accountId: string;
    };
    funnelId?: string;
    entryStepId?: string;
    existingSessionId?: string;
    /**
     * Current URL for session synchronization (browser back/forward handling)
     * If not provided, SDK will automatically use window.location.href
     * @example '/checkout', 'https://store.com/payment'
     */
    currentUrl?: string;
    /**
     * A/B test variant ID extracted from injected HTML
     * Used to track which variant the user is seeing in an A/B test
     * @example 'step_1765015842897_variant_1'
     */
    funnelVariantId?: string;
    /**
     * Funnel step ID extracted from injected HTML
     * Used to track which step the user is on
     * @example 'step_1765015842897'
     */
    funnelStepId?: string;
    /**
     * 🎯 Draft/preview mode flag (explicit from SDK, more robust than URL parsing)
     * When true, uses draft/staging data instead of production data
     */
    draft?: boolean;
    /**
     * 🎯 Funnel tracking enable/disable flag (explicit from SDK, more robust than URL parsing)
     * When false, disables all funnel tracking events (useful for iframed previews in config editor)
     */
    funnelTracking?: boolean;
    /**
     * 🎯 Funnel environment override ('staging' | 'production')
     * Determines which funnel config to use
     */
    funnelEnv?: 'staging' | 'production';
    /**
     * 🎯 Tagada client environment override ('production' | 'development' | 'local')
     * Forces specific API environment
     */
    tagadaClientEnv?: 'production' | 'development' | 'local';
    /**
     * 🎯 Tagada client base URL override
     * Forces custom API base URL (e.g., for local development with ngrok)
     */
    tagadaClientBaseUrl?: string;
    /**
     * 🌍 Display currency override (e.g., 'USD', 'EUR')
     * Forces specific currency for display purposes
     */
    currency?: string;
    /**
     * 🌍 Display locale override (e.g., 'en', 'fr')
     * Forces specific locale for display purposes
     */
    locale?: string;
}

export interface FunnelInitializeResponse {
    success: boolean;
    context?: SimpleFunnelContext;
    error?: string;
}

export interface FunnelNavigateRequest {
    sessionId: string;
    event: FunnelAction;
    contextUpdates?: Partial<SimpleFunnelContext>;
    /**
     * Current URL for URL-to-Step mapping (browser back/forward sync)
     * Automatically extracted from event.currentUrl or window.location.href
     */
    currentUrl?: string;
    /**
     * Funnel ID for session recovery when session expires
     * If session is not found and funnelId is provided, a new session will be created
     */
    funnelId?: string;
    /**
     * Funnel step ID (from SDK injection/URL params)
     * Used to sync session state before navigation
     */
    funnelStepId?: string;
    /**
     * Funnel variant ID (from SDK injection/URL params for A/B testing)
     * Used to sync session state before navigation
     */
    funnelVariantId?: string;
    /**
     * Fire and forget mode - queues navigation to QStash and returns immediately
     * No response data needed, just acknowledgment that request was queued
     * When true, result will only contain queued status and sessionId (no URL or stepId)
     */
    fireAndForget?: boolean;
    /**
     * ✅ Customer tags to set (merged with existing customer tags)
     * @example ['segment:vip', 'cart_value:high']
     */
    customerTags?: string[];
    /**
     * ✅ Device ID for geo/device tag enrichment (optional)
     * @example 'dev_abc123xyz'
     */
    deviceId?: string;
}

export interface FunnelNavigateResponse {
    success: boolean;
    result?: {
        stepId?: string;
        url?: string;
        /**
         * New session ID if session was recovered (expired/removed)
         * SDK should update its sessionId if this is present
         */
        sessionId?: string;
        tracking?: {
            from: string;
            to: string;
            event: string;
            timestamp: string;
        };
        /**
         * Fire-and-forget response: indicates navigation was queued
         * When present, stepId and url will be undefined
         */
        queued?: boolean;
    };
    error?: string;
}

export interface FunnelContextUpdateRequest {
    contextUpdates: Partial<SimpleFunnelContext>;
}

export interface FunnelContextUpdateResponse {
    success: boolean;
    error?: string;
}

export class FunnelResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Initialize a funnel session
     */
    async initialize(request: FunnelInitializeRequest): Promise<FunnelInitializeResponse> {
        return this.apiClient.post('/api/v1/funnel/initialize', request);
    }

    /**
     * Navigate to next step in funnel
     */
    async navigate(request: FunnelNavigateRequest): Promise<FunnelNavigateResponse> {
        return this.apiClient.post('/api/v1/funnel/navigate', request);
    }

    /**
     * Update funnel context
     */
    async updateContext(sessionId: string, request: FunnelContextUpdateRequest): Promise<FunnelContextUpdateResponse> {
        return this.apiClient.patch(`/api/v1/funnel/context/${sessionId}`, request);
    }

    /**
     * End funnel session
     */
    async endSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.delete(`/api/v1/funnel/session/${sessionId}`);
    }

    /**
     * Get funnel session by ID
     * @param sessionId - The session ID to fetch
     * @param currentUrl - Optional current URL for session synchronization on page load
     */
    async getSession(sessionId: string, currentUrl?: string, includeDebugData?: boolean): Promise<{ success: boolean; context?: SimpleFunnelContext; debugData?: Record<string, unknown>; error?: string }> {
        const params = new URLSearchParams();
        if (currentUrl) {
            params.append('currentUrl', currentUrl);
        }
        if (includeDebugData) {
            params.append('includeDebugData', 'true');
        }
        const url = `/api/v1/funnel/session/${sessionId}${params.toString() ? `?${params}` : ''}`;
        return this.apiClient.get(url);
    }
}

